import { useOutletContext } from 'react-router'
import { ErrorBox } from '@/components/commons/error-box'
import { Container } from '@/components/ui/container'

import { AlertTriangleIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { useAuthStore } from '@/features/auth/store/store'
import type { OpsVettingURLParams } from '@/features/ops-vetting/types.ts'
import { SubHeaderBanner } from '@/features/photoset/components/sub-header-banner'
import { ServiceVettingCriteriaGalleryContainer } from '@/features/service-vetting/components/service-vetting-criteria-gallery-container'
import { ServiceVettingCriteriaHero } from '@/features/service-vetting/components/service-vetting-criteria-hero'
import { ServiceVettingCriteriaHostExpertise } from '@/features/service-vetting/components/service-vetting-criteria-host-expertise'
import { ServiceVettingCriteriaOfferings } from '@/features/service-vetting/components/service-vetting-criteria-offerings'
import { ServiceVettingCriteriaSkeleton } from '@/features/service-vetting/components/service-vetting-criteria-skeleton'
import { useGetService } from '@/features/service-vetting/query/use-get-service.ts'
import { useServiceVettingError } from '@/features/service-vetting/store/use-service-vetting-error.ts'
import { useTranslation } from '@/translations/i18n'

export const ServiceCriteriaVetting = () => {
	const { urlParams } = useOutletContext<{ urlParams: OpsVettingURLParams }>()
	const { t } = useTranslation()
	const serviceQuery = useGetService(urlParams.id)

	const showBanner = useServiceVettingError((state) => state.showBanner)
	const user = useAuthStore((state) => state.user)

	const hideAction =
		serviceQuery.remappedData?.status !== 'under_review' ||
		user?.role === 'read_only' ||
		serviceQuery.remappedData?.is_host_modified

	const isExpertiseDeclined =
		serviceQuery.remappedData?.expertise.status === 'declined'
	const isProPhotographyRequired =
		!!serviceQuery.remappedData?.is_pro_photography_required
	const lackPhotoVariety = !!serviceQuery.remappedData?.gallery.flags.some(
		(flag) => flag.key_name === 'lack_photo_variety'
	)
	if (serviceQuery.isPending) {
		return <ServiceVettingCriteriaSkeleton />
	}

	if (serviceQuery.isError) {
		return (
			<Container fullWidth>
				<ErrorBox
					icon={<AlertTriangleIcon />}
					title={t('errors:default')}
				/>
			</Container>
		)
	}

	return (
		<ScrollArea scrollbar={<Scrollbar />}>
			{serviceQuery.remappedData && (
				<>
					<Container fullWidth>
						<ServiceVettingCriteriaHero
							service={serviceQuery.remappedData}
						/>
					</Container>
					{showBanner && (
						<SubHeaderBanner
							subtitle={t(
								'vetting:criteria.commons.error_banner.subtitle'
							)}
							title={t(
								'vetting:criteria.commons.error_banner.title'
							)}
							type={'danger'}
						/>
					)}
					<Container fullWidth>
						<div className='flex w-full flex-col gap-6'>
							<ServiceVettingCriteriaHostExpertise
								service={serviceQuery.remappedData}
							/>
							<ServiceVettingCriteriaOfferings
								service={serviceQuery.remappedData}
							/>
							<ServiceVettingCriteriaGalleryContainer
								galleryId={serviceQuery.remappedData.gallery.id}
								hideAction={hideAction}
								isExpertiseDeclined={isExpertiseDeclined}
								isProPhotographyRequired={
									isProPhotographyRequired
								}
								lackPhotoVariety={lackPhotoVariety}
								serviceId={urlParams.id}
							/>
						</div>
					</Container>
				</>
			)}
		</ScrollArea>
	)
}
